import { jsxs, jsx } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useMergedRefs } from "../internal/refs.js";
const NESTED_CONTROL = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  "summary",
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="textbox"]'
].join(",");
function isNestedControlClick(event) {
  const row = event.currentTarget;
  const target = event.target;
  if (!(target instanceof Element) || !row.contains(target)) return true;
  const control = target.closest(NESTED_CONTROL);
  return control !== null && control !== row && row.contains(control);
}
function hasTextSelectionIn(row) {
  const selection = typeof window.getSelection === "function" ? window.getSelection() : null;
  return !!selection && !selection.isCollapsed && !!selection.anchorNode && row.contains(selection.anchorNode);
}
function focusSiblingRow(row, direction) {
  let next = direction === 1 ? row.nextElementSibling : row.previousElementSibling;
  while (next && !(next instanceof HTMLTableRowElement && next.dataset.interactive === "true")) {
    next = direction === 1 ? next.nextElementSibling : next.previousElementSibling;
  }
  if (!next) return false;
  next.focus();
  return true;
}
function defaultCell(row, key) {
  if (row === null || typeof row !== "object") return null;
  const value = row[key];
  return typeof value === "string" || typeof value === "number" ? value : null;
}
function columnLabel(column) {
  if (column.label !== void 0) return column.label;
  return typeof column.header === "string" || typeof column.header === "number" ? String(column.header) : void 0;
}
function useScrollOverflow(wrapRef, tableRef) {
  const [overflowing, setOverflowing] = React.useState(false);
  React.useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return void 0;
    const check = () => setOverflowing(wrap.scrollWidth - wrap.clientWidth > 1 || wrap.scrollHeight - wrap.clientHeight > 1);
    check();
    window.addEventListener("resize", check);
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(check) : void 0;
    observer?.observe(wrap);
    if (tableRef.current) observer?.observe(tableRef.current);
    return () => {
      window.removeEventListener("resize", check);
      observer?.disconnect();
    };
  }, [wrapRef, tableRef]);
  return overflowing;
}
function DataTableRender(props, ref) {
  const {
    columns,
    rows,
    getRowId,
    phoneMode,
    currentRowId,
    defaultCurrentRowId = null,
    onRowClick,
    rowHint = "Press Enter to open",
    density = "default",
    stickyFirstColumn = false,
    caption,
    scrollRegionLabel = "Table",
    maxHeight,
    empty,
    rowClassName,
    tableClassName,
    className,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    style,
    ...rest
  } = props;
  const controlled = currentRowId !== void 0;
  const [uncontrolledCurrent, setUncontrolledCurrent] = React.useState(defaultCurrentRowId);
  const current = controlled ? currentRowId : uncontrolledCurrent;
  const interactive = typeof onRowClick === "function";
  const cards = phoneMode === "cards";
  const role = (name) => cards ? name : void 0;
  const captionId = React.useId();
  const hintId = React.useId();
  const hasCaption = caption !== void 0 && caption !== null && caption !== false;
  const hasHint = interactive && rowHint !== "";
  const wrapRef = React.useRef(null);
  const tableRef = React.useRef(null);
  const overflowing = useScrollOverflow(wrapRef, tableRef);
  const setWrapRef = useMergedRefs(ref, wrapRef);
  const rowIds = rows.map((row, index) => getRowId(row, index));
  const [focusedRowId, setFocusedRowId] = React.useState(null);
  const tabStopId = !interactive ? null : focusedRowId !== null && rowIds.includes(focusedRowId) ? focusedRowId : current != null && rowIds.includes(current) ? current : rowIds[0] ?? null;
  const activate = (row, rowId, event) => {
    if (!controlled) setUncontrolledCurrent(rowId);
    onRowClick?.(row, rowId, event);
  };
  const handleRowClick = (row, rowId) => (event) => {
    if (event.defaultPrevented || isNestedControlClick(event) || hasTextSelectionIn(event.currentTarget)) return;
    activate(row, rowId, event);
  };
  const handleRowKeyDown = (row, rowId) => (event) => {
    if (event.defaultPrevented || event.target !== event.currentTarget) return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    switch (event.key) {
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault();
        if (!event.repeat) activate(row, rowId, event);
        break;
      case "ArrowDown":
        if (focusSiblingRow(event.currentTarget, 1)) event.preventDefault();
        break;
      case "ArrowUp":
        if (focusSiblingRow(event.currentTarget, -1)) event.preventDefault();
        break;
    }
  };
  const regionName = ariaLabelledBy ?? (hasCaption ? captionId : void 0);
  const regionProps = overflowing ? {
    tabIndex: 0,
    role: "region",
    "aria-label": ariaLabel || (regionName ? void 0 : scrollRegionLabel),
    "aria-labelledby": ariaLabel ? void 0 : regionName
  } : {};
  const hasMaxHeight = maxHeight !== void 0 && maxHeight !== null && maxHeight !== "";
  const wrapStyle = hasMaxHeight ? {
    ...style,
    "--ev-table-max-height": typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight
  } : style;
  const cellClass = (column, index) => cx(column.align === "numeric" && "ev-num", index === 0 && stickyFirstColumn && "ev-sticky-col", column.className) || void 0;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cx("ev-table-wrap", className),
      "data-density": density,
      "data-max-height": hasMaxHeight ? "true" : void 0,
      style: wrapStyle,
      ...regionProps,
      ...rest,
      ref: setWrapRef,
      children: [
        hasHint ? /* @__PURE__ */ jsx("span", { id: hintId, className: "ev-visually-hidden", children: rowHint }) : null,
        /* @__PURE__ */ jsxs(
          "table",
          {
            ref: tableRef,
            className: cx("ev-table", cards && "ev-table--cards", tableClassName),
            role: role("table"),
            "data-phone-mode": phoneMode,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledBy,
            "aria-describedby": ariaDescribedBy,
            children: [
              hasCaption ? /* @__PURE__ */ jsx("caption", { id: captionId, className: "ev-table__caption", children: caption }) : null,
              /* @__PURE__ */ jsx("thead", { role: role("rowgroup"), children: /* @__PURE__ */ jsx("tr", { role: role("row"), children: columns.map((column, index) => /* @__PURE__ */ jsx("th", { scope: "col", role: role("columnheader"), className: cellClass(column, index), children: column.header }, column.key)) }) }),
              /* @__PURE__ */ jsxs("tbody", { role: role("rowgroup"), children: [
                rows.length === 0 && empty !== void 0 && empty !== null && empty !== false ? /* @__PURE__ */ jsx("tr", { role: role("row"), className: "ev-table__empty-row", children: /* @__PURE__ */ jsx("td", { role: role("cell"), colSpan: Math.max(columns.length, 1), className: "ev-table__empty", children: empty }) }) : null,
                rows.map((row, rowIndex) => {
                  const rowId = rowIds[rowIndex];
                  return /* @__PURE__ */ jsx(
                    "tr",
                    {
                      role: role("row"),
                      className: rowClassName?.(row, rowIndex) || void 0,
                      "aria-current": current === rowId ? "true" : void 0,
                      "data-interactive": interactive ? "true" : void 0,
                      tabIndex: interactive ? rowId === tabStopId ? 0 : -1 : void 0,
                      "aria-describedby": hasHint ? hintId : void 0,
                      onClick: interactive ? handleRowClick(row, rowId) : void 0,
                      onKeyDown: interactive ? handleRowKeyDown(row, rowId) : void 0,
                      onFocus: interactive ? (event) => {
                        if (event.target === event.currentTarget) setFocusedRowId(rowId);
                      } : void 0,
                      children: columns.map((column, index) => /* @__PURE__ */ jsx(
                        "td",
                        {
                          role: role("cell"),
                          className: cellClass(column, index),
                          "data-label": cards ? columnLabel(column) : void 0,
                          children: column.render ? column.render(row, rowIndex) : defaultCell(row, column.key)
                        },
                        column.key
                      ))
                    },
                    rowId
                  );
                })
              ] })
            ]
          }
        )
      ]
    }
  );
}
const DataTableBase = React.forwardRef(DataTableRender);
DataTableBase.displayName = "DataTable";
const DataTable = DataTableBase;
export {
  DataTable
};
//# sourceMappingURL=DataTable.js.map
